import React from 'react';
import type { ThemeConfig } from '../schema';
import { resolveContentTheme, type ContentThemeLayers } from './resolveTheme';
import { themeToCssVars } from './themeToCssVars';

export type ContentThemeSkin = 'default' | 'playful';

/** Sparse pieces the Provider computes and stores on context; see `useContentTheme` for the public shape (adds `effectiveColors`). */
interface ResolvedContentThemeContext {
    /** Resolved sparse delta (item layers only) — `undefined` when host tokens win. */
    theme: ThemeConfig | undefined;
    /** Memoized `themeToCssVars(theme)`. */
    cssVars: React.CSSProperties;
    /** From shared-styles cascade (Phase 5); `'default'` until then. */
    skin: ContentThemeSkin;
}

export const ContentThemeContext = React.createContext<ResolvedContentThemeContext>({
    theme: undefined,
    cssVars: {},
    skin: 'default',
});

export interface ContentThemeProviderProps {
    layers: ContentThemeLayers;
    skin?: ContentThemeSkin;
    children: React.ReactNode;
}

/**
 * Resolves the content theme layers once and exposes them via context.
 *
 * Follows the react-performance rule: destructures the stable fields it
 * depends on rather than the whole `layers` object, so a caller that
 * rebuilds `{ ...layers }` inline every render doesn't defeat this memo —
 * callers must still keep `preset`/`config`/`override` themselves
 * referentially stable (memoized) across renders.
 */
export function ContentThemeProvider({
    layers,
    skin,
    children,
}: ContentThemeProviderProps): React.ReactElement {
    const { preset, config, override, useHostTheme } = layers;

    const value = React.useMemo<ResolvedContentThemeContext>(() => {
        const theme = resolveContentTheme({ preset, config, override, useHostTheme });
        return { theme, cssVars: themeToCssVars(theme), skin: skin ?? 'default' };
    }, [preset, config, override, useHostTheme, skin]);

    return <ContentThemeContext.Provider value={value}>{children}</ContentThemeContext.Provider>;
}
